import PropTypes from 'prop-types';
import { useEffect, useState, useRef, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  calculateRemainingTime,
  clearlocalStoreFullfillment,
  getCuttOffDetails,
  getPickupAndDeliveryPage,
  showDateUpdatedToaster
} from '@app/components/pages/checkout/PickUpAndDelivery/utils';
import { useCountDown } from '@app/hooks';
import { ToastContext } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import useCalculateFreightWrapper from '@app/hooks/useCalculateFreightWrapper';
import {
  AVAILABLE_DATE,
  AVAILABLE_MSG,
  RBD_VALUE,
  SHIPPING_CODE,
  SHOP_ONLINE,
  DELIVERY,
  SUMMARY_AND_PAYMENT,
  PICKUP
} from '../../../../constants';
import { parse } from 'date-fns';
import { PICK_UP_TYPE, USER_TYPE_GUEST } from '@app/constants/commonConstants';

const withCountDownWrapper = (WrappedComponent, pageType = PICK_UP_TYPE) => {
  const WithCountDown = props => {
    const {
      fulfillment,
      calculateTime,
      setCalculateTime,
      fulfillmentType,
      estimatedDate,
      shipCode = '',
      deliveryMethods,
      isGuestEnhancedEnabled = false,
      isShouldRunTimer = false
    } = props;
    const { cutOffDateTime, availableByDate, availabilityMessage } =
      estimatedDate ?? {};
    const {
      emptyCutOffTimeListCode,
      isAllCutOffTimeSame: isSameCutOffDateTime
    } = getCuttOffDetails(deliveryMethods);
    const dispatch = useDispatch();
    const current = useSelector(s => s?.orders?.current);
    const delcode = useSelector(
      s =>
        s.orders?.byId?.[current]?.fullfillment?.[SHOP_ONLINE]
          ?.shippingMethodCode
    );
    const { values } = useFormikContext() || {};
    const { taxAndFreightTrigger } = useCalculateFreightWrapper(
      fulfillmentType,
      isGuestEnhancedEnabled,
      pageType
    );
    const requestByDate = useSelector(s => s.orders?.requestByDate);
    const pccHideEstDateForNonAvailableItems = useSelector(
      state => state?.featureFlag?.PCCHideEstDateForNonAvailableItems
    );
    const toastCtx = useContext(ToastContext);
    const [t] = useTranslation();
    const userType = useSelector(s => s?.common?.userType) ?? '';
    const isGuestAndDeliveryFullfilment =
      userType === USER_TYPE_GUEST && fulfillmentType === DELIVERY;
    const taxAndFreight = useSelector(s => s?.checkout?.taxAndFreight) ?? [];
    const prevMinutes = useRef(null);

    const [remainingTime, setRemainingTime] = useState({
      total: 0,
      hours: null,
      minutes: null,
      isReachedCutOff: false
    });

    const { total, isReachedCutOff, hours, minutes } = remainingTime ?? {};

    let pageTypeValues = {
      summaryAndPaymentShippingCode: delcode,
      pickupAndDeliveryShippingCode: values?.[SHOP_ONLINE]?.deliveryMethodCode,
      summaryAndPaymentRBDValue: !!requestByDate
        ? parse(requestByDate?.value, requestByDate?.format, new Date())
        : '',
      pickupAndDeliveryRBDValue: values?.requestByDate,
      deliveryAvailableMsg: '',
      pickUpAvailableMsg: availabilityMessage,
      pickupAvailableMsg: availabilityMessage,
      dropBoxAvailableMsg: availabilityMessage,
      deliveryAvailableDate: '',
      pickUpAvailableDate: availableByDate,
      pickupAvailableDate: availableByDate,
      dropBoxAvailableDate: availableByDate
    };

    const Args = {
      selectedShipmentCode: pageTypeValues[pageType + SHIPPING_CODE],
      formRequestValue: pageTypeValues[pageType + RBD_VALUE],
      isAllCutOffTimeSame: isSameCutOffDateTime,
      cutOFFDateTimeEmptyList: emptyCutOffTimeListCode,
      shipCode,
      fulfillment
    };
    const selectedDelivery = deliveryMethods?.find(fnd =>
      isGuestAndDeliveryFullfilment && pageType === SUMMARY_AND_PAYMENT
        ? fnd
        : fnd.code === Args.selectedShipmentCode ||
          fnd.shipViaCode === Args.selectedShipmentCode
    )?.estimatedDeliveryDate;

    pageTypeValues = {
      ...pageTypeValues,
      deliveryAvailableMsg: selectedDelivery?.availabilityMessage,
      deliveryAvailableDate: selectedDelivery?.availableByDate
    };

    const isGuestDeliveryShipViaCode = useSelector(
      s => !!s.featureFlag?.PCCGuestDeliveryShipViaCode
    );
    const showEstimatedDeliveryDates = useSelector(
      s => s.dealer.showEstimatedDeliveryDates
    );
    useEffect(() => {
      const {
        total,
        hours: remainingHours,
        minutes: remainingMinutes
      } = calculateRemainingTime(cutOffDateTime);
      setRemainingTime(prev => ({
        ...prev,
        hours: remainingHours,
        minutes: remainingMinutes,
        total: total
      }));
    }, [cutOffDateTime]);

    useEffect(() => {
      if (hours + minutes <= 0 && isReachedCutOff && !!cutOffDateTime) {
        getPickupAndDeliveryPage({
          fulfillment,
          dispatch,
          availabilityMessage: pageTypeValues[fulfillment + AVAILABLE_MSG],
          fieldValueObj: {
            fieldValue: Args.formRequestValue,
            shipmentCode: Args.selectedShipmentCode
          },
          showToasterFulFillment: (
            isRBDFieldUpdate,
            prevAvailabilityMessage,
            currentAvailabilityMessage
          ) => {
            showDateUpdatedToaster({
              fulfillmentType,
              toastCtx,
              t,
              isRBDFieldUpdate,
              prevAvailabilityMessage,
              currentAvailabilityMessage,
              isGuestDeliveryShipViaCode,
              isGuestEnhancedEnabled,
              showEstimatedDeliveryDates,
              taxAndFreight,
              isGuestAndDeliveryFullfilment,
              pccHideEstDateForNonAvailableItems,
              ...Args
            });
            clearlocalStoreFullfillment({
              availableByDate: pageTypeValues[fulfillment + AVAILABLE_DATE],
              prevAvailabilityMessage,
              currentAvailabilityMessage,
              ...Args
            });
          },
          pageType,
          isGuestAndDeliveryFullfilment,
          taxAndFreightTrigger,
          taxAndFreight,
          isGuestEnhancedEnabled
        });
        setRemainingTime(prev => ({
          ...prev,
          isReachedCutOff: !prev.isReachedCutOff
        }));
      }
    }, [
      hours,
      minutes,
      fulfillment,
      isReachedCutOff,
      availabilityMessage,
      dispatch,
      availableByDate,
      requestByDate,
      fulfillmentType,
      values,
      toastCtx,
      t,
      cutOffDateTime
    ]);

    const updateCalculateTime = ({
      setTime,
      hours,
      minutes,
      isUptatedTimer,
      shipCode
    }) =>
      setTime(prev => {
        return {
          ...prev,
          ...(prev.hours !== hours && {
            hours: hours
          }),
          ...(hours + minutes <= 0 && {
            isReachedCutOff: true
          }),
          ...(prev.minutes !== minutes && {
            minutes: minutes
          }),
          ...(!!isUptatedTimer && { isUptatedTimer: isUptatedTimer }),
          ...(!!shipCode && !prev.isUptatedTimer && { shipCode })
        };
      });

    const isUpdateTimer = (hours, minutes) => {
      return (
        Math.abs(hours + minutes) >= 0 &&
        !isReachedCutOff &&
        prevMinutes.current !== minutes &&
        availableByDate
      );
    };
    useCountDown(
      total,
      (hours, minutes) => {
        if (isUpdateTimer(hours, minutes)) {
          prevMinutes.current = minutes;
          updateCalculateTime({ setTime: setRemainingTime, hours, minutes });
          !!cutOffDateTime &&
            setCalculateTime &&
            updateCalculateTime({
              setTime: setCalculateTime,
              hours,
              minutes,
              isUptatedTimer: true,
              shipCode
            });
        }
      },
      isShouldRunTimer
    );
    return (
      <WrappedComponent
        {...props}
        remainingTime={fulfillment === PICKUP ? remainingTime : calculateTime}
        isGuestAndDeliveryFullfilment={isGuestAndDeliveryFullfilment}
      />
    );
  };
  WithCountDown.propTypes = {
    estimatedDate: PropTypes.shape({
      availabilityMessage: PropTypes.string,
      orderByTime: PropTypes.string,
      availableByDate: PropTypes.string,
      cutOffDateTime: PropTypes.string
    }),
    className: PropTypes.string,
    fulfillment: PropTypes.string,
    shipCode: PropTypes.string,
    fulfillmentType: PropTypes.string,
    calculateTime: PropTypes.shape({
      hours: PropTypes.number,
      minutes: PropTypes.number,
      total: PropTypes.number,
      isReachedCutOff: PropTypes.bool
    }),
    isAllCutOffTimeSame: PropTypes.bool,
    setCalculateTime: PropTypes.func,
    deliveryMethods: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        displayName: PropTypes.string,
        value: PropTypes.string,
        default: PropTypes.bool
      })
    ),
    isGuestEnhancedEnabled: PropTypes.bool,
    isShouldRunTimer: PropTypes.bool
  };
  return WithCountDown;
};

export default withCountDownWrapper;
